import React from 'react';
import { motion } from 'framer-motion';
import { Check, Book, Layout, Shield, Zap, FileText, Settings, BookOpen, Users, Briefcase, FileEdit, MessageSquare, CheckSquare, Package } from 'lucide-react';
const learningOutcomes = [{
  title: 'Más claridad clínica',
  desc: 'Tomá decisiones con más seguridad profesional.',
  icon: Zap
}, {
  title: 'Más organización',
  desc: 'Ordená entrevistas, evaluaciones e intervenciones.',
  icon: Layout
}, {
  title: 'Menos improvisación',
  desc: 'Trabajá con estructuras concretas y aplicables.',
  icon: Settings
}, {
  title: 'Más confianza profesional',
  desc: 'Sentite más segura en entrevistas y devoluciones.',
  icon: Shield
}, {
  title: 'Más herramientas prácticas',
  desc: 'Modelos, recursos y materiales listos para usar.',
  icon: FileText
}, {
  title: 'Más estructura profesional',
  desc: 'Organizá tu práctica de forma más clara y sostenible.',
  icon: Briefcase
}];
const modules = [{
  num: '01',
  title: 'IDENTIDAD PROFESIONAL',
  desc: 'Aprendé a construir seguridad clínica y fortalecer tu rol profesional.',
  tags: ['inseguridad clínica', 'síndrome impostor', 'identidad profesional', 'confianza clínica', 'mirada profesional'],
  color: 'bg-violet-50',
  textColor: 'text-violet-700'
}, {
  num: '02',
  title: 'DISPOSITIVO CLÍNICO',
  desc: 'Cómo organizar tu práctica y construir una estructura profesional clara.',
  tags: ['consultorio', 'encuadre', 'organización clínica', 'matrícula y obras sociales', 'recursos básicos'],
  color: 'bg-teal-50',
  textColor: 'text-teal-700'
}, {
  num: '03',
  title: 'PRIMERAS ENTREVISTAS',
  desc: 'Aprendé a sostener entrevistas y primeras consultas con más claridad.',
  tags: ['motivo de consulta', 'entrevistas familiares', 'primeras observaciones', 'límites', 'acuerdos iniciales'],
  color: 'bg-blue-50',
  textColor: 'text-blue-700'
}, {
  num: '04',
  title: 'EVALUACIÓN PSICOPEDAGÓGICA',
  desc: 'Cómo pensar clínicamente la evaluación más allá de las técnicas.',
  tags: ['observación', 'hipótesis clínicas', 'evaluación como proceso', 'mirada integral', 'análisis clínico'],
  color: 'bg-rose-50',
  textColor: 'text-rose-700'
}, {
  num: '05',
  title: 'INTERVENCIÓN',
  desc: 'Herramientas para organizar intervenciones y primeras sesiones.',
  tags: ['planificación', 'estrategias', 'acompañamiento clínico', 'recursos prácticos', 'intervención psicopedagógica'],
  color: 'bg-amber-50',
  textColor: 'text-amber-700'
}, {
  num: '06',
  title: 'INFORMES Y DEVOLUCIONES',
  desc: 'Aprendé a escribir informes claros y profesionales.',
  tags: ['redacción clínica', 'devoluciones', 'escritura profesional', 'organización de información', 'claridad comunicacional'],
  color: 'bg-indigo-50',
  textColor: 'text-indigo-700'
}, {
  num: '07',
  title: 'FAMILIAS Y LÍMITES',
  desc: 'Cómo sostener vínculos profesionales sin desbordarte.',
  tags: ['comunicación con familias', 'límites', 'orientación', 'sostén emocional', 'trabajo interdisciplinario'],
  color: 'bg-emerald-50',
  textColor: 'text-emerald-700'
}, {
  num: '08',
  title: 'ORGANIZACIÓN PROFESIONAL',
  desc: 'Construí una práctica clínica más organizada y sostenible.',
  tags: ['organización clínica', 'herramientas', 'crecimiento profesional', 'recursos de trabajo', 'práctica sostenible'],
  color: 'bg-fuchsia-50',
  textColor: 'text-fuchsia-700'
}];
const toolkit = [{
  title: 'PLANTILLAS CLÍNICAS EDITABLES',
  icon: FileEdit,
  items: ['anamnesis', 'observación', 'seguimiento', 'objetivos', 'informes', 'PPI']
}, {
  title: 'FRASES PROFESIONALES PARA CLÍNICA Y DEVOLUCIONES',
  icon: MessageSquare,
  items: ['entrevistas', 'devoluciones', 'reuniones', 'orientación a familias', 'límites profesionales']
}, {
  title: 'CHECKLISTS CLÍNICOS IMPRIMIBLES',
  icon: CheckSquare,
  items: ['primera entrevista', 'evaluación', 'organización clínica', 'informes', 'seguimiento']
}, {
  title: 'KIT “PRIMEROS RECURSOS PARA CONSULTORIO”',
  icon: Package,
  items: ['materiales iniciales', 'organización básica', 'recursos visuales', 'estructura de trabajo', 'herramientas de uso diario']
}, {
  title: 'ERRORES FRECUENTES EN LOS PRIMEROS AÑOS CLÍNICOS',
  icon: Shield,
  items: ['inseguridad profesional', 'sobreexigencia', 'límites', 'organización', 'errores en entrevistas', 'errores en informes y evaluaciones']
}];
export function ProductDetailsSection() {
  return <div className="bg-white py-24" data-id="element-155">
      {/* Value Prop */}
      <section className="container mx-auto px-4 max-w-5xl mb-32" data-id="element-156">
        <div className="text-center mb-16" data-id="element-157">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy-900 mb-4 uppercase tracking-tight" data-id="element-158">
            ¿Y si este fuera el momento de sentirte más segura en tu práctica?
          </h2>
          <div className="inline-block bg-violet-100 text-violet-800 px-4 py-1.5 rounded-full font-bold text-sm tracking-wide mb-6" data-id="element-159">
            🏆 EL MEGAPACK
          </div>
          <p className="text-xl text-slate-600 mb-2" data-id="element-160">
            Una biblioteca profesional completa para fortalecer tu práctica
            clínica psicopedagógica.
          </p>
          <p className="text-lg font-medium text-teal-600" data-id="element-161">
            8 módulos + Kit PPI + Kit de herramientas profesionales
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-100" data-id="element-162">
          <div data-id="element-163">
            <h3 className="font-bold text-navy-900 mb-6 uppercase tracking-wide text-sm" data-id="element-164">
              Este material fue creado para ayudarte a:
            </h3>
            <ul className="space-y-4" data-id="element-165">
              {['organizar tu práctica clínica', 'intervenir con más claridad', 'estructurar entrevistas y evaluaciones', 'fortalecer tu mirada clínica', 'trabajar con más seguridad profesional', 'ahorrar tiempo con recursos listos para usar'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-166">
                  <Check className="w-5 h-5 text-teal-500 shrink-0 mt-0.5" data-id="element-167" />
                  <span className="text-slate-700 font-medium" data-id="element-168">{item}</span>
                </li>)}
            </ul>
          </div>
          <div data-id="element-169">
            <h3 className="font-bold text-navy-900 mb-6 uppercase tracking-wide text-sm" data-id="element-170">
              Incluye:
            </h3>
            <ul className="space-y-4" data-id="element-171">
              {['8 módulos completos', 'Kit de PPI para psicopedagogas', 'Kit de herramientas profesionales', 'Recursos editables', 'Material imprimible', 'Acceso inmediato'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-172">
                  <div className="w-5 h-5 rounded-full bg-violet-200 flex items-center justify-center shrink-0 mt-0.5" data-id="element-173">
                    <Check className="w-3 h-3 text-violet-700" data-id="element-174" />
                  </div>
                  <span className="text-slate-700 font-medium" data-id="element-175">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="container mx-auto px-4 max-w-5xl mb-32" data-id="element-176">
        <div className="text-center mb-12" data-id="element-177">
          <span className="text-teal-600 font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-178">
            🌸 Qué vas a aprender
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy-900 uppercase tracking-tight" data-id="element-179">
            Herramientas reales para la clínica
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-180">
          {learningOutcomes.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow" data-id="element-181">
              <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 mb-4" data-id="element-182">
                <item.icon className="w-6 h-6" data-id="element-183" />
              </div>
              <h3 className="font-bold text-navy-900 text-lg mb-2" data-id="element-184">
                {item.title}
              </h3>
              <p className="text-slate-600" data-id="element-185">{item.desc}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Modules Grid */}
      <section className="container mx-auto px-4 max-w-5xl mb-32" data-id="element-186">
        <div className="text-center mb-12" data-id="element-187">
          <span className="text-violet-600 font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-188">
            🌸 8 Módulos Completos
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy-900 uppercase tracking-tight mb-4" data-id="element-189">
            Más de 200 páginas. Todo organizado.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-190">
          {modules.map((mod, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className={`rounded-3xl p-8 border border-slate-100 ${mod.color}`} data-id="element-191">
              <div className="flex items-center gap-3 mb-4" data-id="element-192">
                <BookOpen className={`w-6 h-6 ${mod.textColor}`} data-id="element-193" />
                <span className={`font-bold tracking-widest text-sm uppercase ${mod.textColor}`} data-id="element-194">
                  Módulo {mod.num}
                </span>
              </div>
              <h3 className="font-bold text-navy-900 text-xl mb-3 uppercase tracking-tight" data-id="element-195">
                {mod.title}
              </h3>
              <p className="text-slate-700 mb-6 font-medium" data-id="element-196">{mod.desc}</p>
              <div className="flex flex-wrap gap-2" data-id="element-197">
                {mod.tags.map((tag, j) => <span key={j} className="bg-white/60 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1" data-id="element-198">
                    <span className="text-[10px]" data-id="element-199">🌸</span> {tag}
                  </span>)}
              </div>
            </motion.div>)}
        </div>

        <div className="bg-slate-900 rounded-2xl p-6 text-center" data-id="element-200">
          <h4 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-201">
            Cada módulo incluye
          </h4>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-202">
            {['herramientas prácticas', 'ejemplos reales', 'modelos aplicables', 'checklists', 'estructuras claras', 'recursos visuales'].map((item, i) => <span key={i} className="text-white text-sm font-medium flex items-center gap-1.5" data-id="element-203">
                <span className="text-teal-400" data-id="element-204">🌸</span> {item}
              </span>)}
          </div>
        </div>
      </section>

      {/* Inside Look (Placeholders) */}
      <section className="container mx-auto px-4 max-w-6xl mb-32" data-id="element-205">
        <div className="text-center mb-12" data-id="element-206">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy-900 uppercase tracking-tight mb-2" data-id="element-207">
            👀 Mirá por dentro
          </h2>
          <p className="text-slate-600 text-lg" data-id="element-208">
            Páginas reales del material que vas a recibir
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-id="element-209">
          {['entrevistas', 'evaluación', 'intervención', 'informes', 'PPI', 'recursos editables'].map((label, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-200 shadow-sm flex items-center justify-center p-6 text-center relative overflow-hidden group" data-id="element-210">
              <div className="absolute inset-0 bg-white/40 group-hover:bg-transparent transition-colors" data-id="element-211" />
              <span className="relative z-10 font-bold text-slate-500 uppercase tracking-widest text-sm" data-id="element-212">
                {label}
              </span>
            </div>)}
        </div>
      </section>

      {/* Toolkit Section */}
      <section className="container mx-auto px-4 max-w-4xl" data-id="element-213">
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl p-8 md:p-12 border border-violet-100" data-id="element-214">
          <div className="text-center mb-12" data-id="element-215">
            <span className="text-violet-600 font-bold tracking-widest text-sm uppercase mb-2 block" data-id="element-216">
              🎁 Kit de herramientas profesionales
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy-900 uppercase tracking-tight mb-4" data-id="element-217">
              Y además te llevás recursos complementarios editables.
            </h2>
          </div>

          <div className="space-y-8" data-id="element-218">
            {toolkit.map((item, i) => <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-violet-100/50" data-id="element-219">
                <div className="flex items-center gap-3 mb-4" data-id="element-220">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600" data-id="element-221">
                    <item.icon className="w-5 h-5" data-id="element-222" />
                  </div>
                  <h3 className="font-bold text-navy-900 text-lg uppercase tracking-tight" data-id="element-223">
                    🌸 {item.title}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2 pl-13" data-id="element-224">
                  {item.items.map((tag, j) => <span key={j} className="bg-slate-100 text-slate-600 text-sm font-medium px-3 py-1 rounded-full" data-id="element-225">
                      {tag}
                    </span>)}
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}